import { type BackupRestoreResult, sessionKeys } from '@taler/api-client'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'

export interface RestoreBackupInput {
  readonly ledgerName: string
  readonly file: File
}

export interface BackupRestoreOutcome {
  readonly ledgerId: string
  readonly result: BackupRestoreResult
}

export interface UseBackupRestore {
  readonly isRestoring: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly outcome: ComputedRef<BackupRestoreOutcome | undefined>
  reset(): void
  restore(input: RestoreBackupInput): void
}

/** One server call creates the ledger and restores the file into it, atomically. */
export function useBackupRestore(): UseBackupRestore {
  const api = useApi()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (input: RestoreBackupInput): Promise<BackupRestoreOutcome> => {
      const created = await api.session.createLedgerFromBackup(input.ledgerName, input.file)
      return { ledgerId: created.id, result: created.restored }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sessionKeys.ledgers() })
    },
  })

  return {
    isRestoring: computed(() => mutation.isPending.value),
    error: computed(() => mutation.error.value),
    outcome: computed(() => mutation.data.value),
    reset: () => mutation.reset(),
    restore: (input) => mutation.mutate(input),
  }
}
